<template>
  <div>
    <el-dialog
      :title="applyDialogTitle"
      :visible.sync="applyDialogVisible"
      width="900px"
      top="6vh"
      append-to-body
      :modal-append-to-body="true"
      :close-on-click-modal="false"
      :z-index="3000"
      custom-class="part-request-apply-dialog"
      @closed="handleClosed"
    >
      <el-form ref="applyFormRef" :model="applyForm" label-width="100px" size="small" :disabled="applyDialogMode === 'view'">
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="关联设备">
              <el-input v-model="applyForm.deviceName" placeholder="请选择设备" readonly>
                <el-button
                  v-if="applyDialogMode !== 'view' && !deviceLocked"
                  slot="append"
                  @click="$refs.deviceDlg.open()"
                >选择</el-button>
              </el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="申请来源" prop="sourceType" :rules="[{ required: true, message: '请选择来源', trigger: 'change' }]">
              <el-select
                v-model="applyForm.sourceType"
                placeholder="请选择"
                style="width: 100%"
                :disabled="applyDialogMode === 'view' || sourceLocked"
              >
                <el-option label="维修工单" :value="1" />
                <el-option label="设备维保" :value="2" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item label="来源单号">
              <el-input
                v-model="applyForm.sourceId"
                placeholder="工单/维保计划等业务单号（可选）"
                maxlength="64"
                show-word-limit
                :disabled="applyDialogMode === 'view' || sourceLocked"
              />
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item label="申请原因">
              <el-input v-model="applyForm.applyReason" type="textarea" :rows="2" placeholder="请输入申请原因" maxlength="512" show-word-limit />
            </el-form-item>
          </el-col>
        </el-row>
        <div class="detail-toolbar">
          <span class="detail-label">备件明细</span>
          <el-button v-if="applyDialogMode !== 'view'" type="primary" size="mini" icon="el-icon-plus" @click="openPartPicker">添加备件</el-button>
        </div>
        <el-table :data="applyForm.detailList" border size="small" style="width: 100%">
          <el-table-column type="index" label="序号" width="50" align="center" />
          <el-table-column prop="partCode" label="编码" min-width="110" show-overflow-tooltip />
          <el-table-column prop="partName" label="名称" min-width="140" show-overflow-tooltip />
          <el-table-column prop="specification" label="规格" width="100" show-overflow-tooltip />
          <el-table-column prop="currentStock" label="当前库存" width="90" align="center">
            <template slot-scope="scope">{{ scope.row.currentStock != null ? scope.row.currentStock : '-' }}</template>
          </el-table-column>
          <el-table-column label="申请数量" width="120" align="center">
            <template slot-scope="scope">
              <el-input-number
                v-if="applyDialogMode !== 'view'"
                v-model="scope.row.requestQty"
                :min="1"
                :precision="0"
                :step="1"
                controls-position="right"
                size="mini"
                style="width: 100%"
              />
              <span v-else>{{ scope.row.requestQty }}</span>
            </template>
          </el-table-column>
          <el-table-column label="备注" min-width="100">
            <template slot-scope="scope">
              <el-input v-if="applyDialogMode !== 'view'" v-model="scope.row.remark" maxlength="256" size="mini" />
              <span v-else>{{ scope.row.remark || '-' }}</span>
            </template>
          </el-table-column>
          <el-table-column v-if="applyDialogMode !== 'view'" label="操作" width="70" align="center">
            <template slot-scope="scope">
              <el-button type="text" size="mini" style="color: #f56c6c" @click="removeDetail(scope.$index)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-form>
      <span v-if="applyDialogMode !== 'view'" slot="footer">
        <el-button @click="applyDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="applySubmitting" @click="submitApply">提交</el-button>
      </span>
      <span v-else slot="footer">
        <el-button type="primary" @click="applyDialogVisible = false">关闭</el-button>
      </span>
    </el-dialog>

    <select-device-dialog ref="deviceDlg" @select="onDeviceSelected" />
    <select-spare-part-dialog ref="partDlg" @select="onPartSelected" />
  </div>
</template>

<script>
import { getById, add, updateApply } from '../api'
import { getUserInfo } from '@/api/system/user'
import SelectDeviceDialog from '../../parts-management/components/SelectDeviceDialog.vue'
import SelectSparePartDialog from './SelectSparePartDialog.vue'

/**
 * 备件领用申请弹窗（新建/修改/详情），与 part-request 列表页新建一致；支持关联设备锁定（外部带入设备）。
 */
export default {
  name: 'PartApplyFormDialog',
  components: {
    SelectDeviceDialog,
    SelectSparePartDialog,
  },
  data() {
    return {
      applyDialogVisible: false,
      applyDialogMode: 'add',
      applySubmitting: false,
      deviceLocked: false,
      /** 外部带入来源时锁定申请来源、来源单号 */
      sourceLocked: false,
      applyForm: {
        id: null,
        deviceId: null,
        deviceName: '',
        sourceType: 1,
        sourceId: '',
        applyReason: '',
        detailList: [],
      },
    }
  },
  computed: {
    applyDialogTitle() {
      if (this.applyDialogMode === 'add') return '新建备件领用申请'
      if (this.applyDialogMode === 'edit') return '修改申请'
      return '申请详情'
    },
  },
  methods: {
    /**
     * @param {Object} opts
     * @param {'add'|'edit'|'view'} [opts.mode='add']
     * @param {boolean} [opts.deviceLocked=false] 为 true 时不显示设备「选择」，仅展示带入的设备
     * @param {boolean} [opts.sourceLocked=false] 为 true 时申请来源、来源单号不可改（如维保页领用备件）
     * @param {Object} [opts.preset] 新建预填：deviceId, deviceName, sourceType, sourceId, applyReason
     * @param {Object} [opts.row] 修改/详情时的列表行（须含 id）
     */
    async open(opts = {}) {
      const { mode = 'add', deviceLocked = false, sourceLocked = false, preset = {}, row } = opts
      this.applyDialogMode = mode
      this.deviceLocked = !!deviceLocked
      this.sourceLocked = !!sourceLocked
      this.resetApplyForm()
      if (preset && typeof preset === 'object') {
        if (preset.deviceId != null) this.applyForm.deviceId = preset.deviceId
        if (preset.deviceName != null) this.applyForm.deviceName = preset.deviceName
        if (preset.sourceType != null) this.applyForm.sourceType = preset.sourceType
        if (preset.sourceId != null) this.applyForm.sourceId = String(preset.sourceId)
        if (preset.applyReason != null) this.applyForm.applyReason = preset.applyReason
      }
      if (mode === 'add') {
        this.applyDialogVisible = true
        return
      }
      if (!row || row.id == null || row.id === '') {
        this.$message.warning('缺少申请单信息')
        return
      }
      try {
        const res = await getById(row.id)
        const body = res && res.data
        const dto = body && body.data
        if (!dto || !dto.id) {
          this.$message.error('加载详情失败')
          return
        }
        this.applyForm.id = dto.id
        this.applyForm.deviceId = dto.deviceId
        this.applyForm.deviceName = dto.deviceName || ''
        this.applyForm.sourceType = dto.sourceType
        this.applyForm.sourceId = dto.sourceId || ''
        this.applyForm.applyReason = dto.applyReason || ''
        this.applyForm.detailList = (dto.detailList || []).map((d) => ({
          id: d.id,
          partId: d.partId,
          partCode: d.partCode,
          partName: d.partName,
          specification: d.specification,
          requestQty: d.requestQty,
          currentStock: d.currentStock,
          partType: d.partType,
          remark: d.remark || '',
        }))
        this.applyDialogVisible = true
      } catch (e) {
        this.$message.error('加载详情失败')
      }
    },
    resetApplyForm() {
      this.applyForm = {
        id: null,
        deviceId: null,
        deviceName: '',
        sourceType: 1,
        sourceId: '',
        applyReason: '',
        detailList: [],
      }
      this.$nextTick(() => {
        if (this.$refs.applyFormRef) {
          this.$refs.applyFormRef.clearValidate()
        }
      })
    },
    handleClosed() {
      this.deviceLocked = false
      this.sourceLocked = false
      this.resetApplyForm()
    },
    onDeviceSelected(dev) {
      if (!dev) return
      const prev = this.applyForm.deviceId
      this.applyForm.deviceId = dev.id
      this.applyForm.deviceName = dev.deviceName
      if (prev != null && String(prev) !== String(dev.id) && this.applyForm.detailList.length) {
        this.applyForm.detailList = []
        this.$message.info('已切换关联设备，请重新添加备件明细')
      }
    },
    openPartPicker() {
      if (!this.applyForm.deviceId) {
        this.$message.warning('请先选择关联设备')
        return
      }
      this.$refs.partDlg.open(this.applyForm.deviceId)
    },
    onPartSelected(part) {
      if (!part) return
      const exists = this.applyForm.detailList.some((r) => String(r.partId) === String(part.id))
      if (exists) {
        this.$message.warning('该备件已在明细中')
        return
      }
      const partType = part.partType != null ? Number(part.partType) : 1
      this.applyForm.detailList.push({
        partId: part.id,
        partCode: part.partCode,
        partName: part.partName,
        specification: part.specification,
        requestQty: 1,
        currentStock: part.stockCount,
        partType,
        remark: '',
      })
    },
    removeDetail(index) {
      this.applyForm.detailList.splice(index, 1)
    },
    async submitApply() {
      if (!this.applyForm.deviceId) {
        this.$message.warning('请选择关联设备')
        return
      }
      if (!this.applyForm.detailList || this.applyForm.detailList.length === 0) {
        this.$message.warning('请至少添加一条备件明细')
        return
      }
      for (let i = 0; i < this.applyForm.detailList.length; i++) {
        const line = this.applyForm.detailList[i]
        const q = line.requestQty
        if (q == null || !Number.isInteger(Number(q)) || q < 1) {
          this.$message.warning(`第 ${i + 1} 行申请数量须为正整数`)
          return
        }
      }
      const userRes = await getUserInfo()
      const userData = userRes.data && userRes.data.data
      const userId = userData ? userData.userId || userData.id || userData.user_id : null
      const userName = userData ? userData.realName || userData.name || userData.nick_name : ''
      const payload = {
        deviceId: this.applyForm.deviceId,
        deviceName: this.applyForm.deviceName,
        sourceType: this.applyForm.sourceType,
        sourceId: this.applyForm.sourceId,
        applyReason: this.applyForm.applyReason,
        applicantId: userId,
        applicantName: userName,
        detailList: this.applyForm.detailList.map((d, idx) => ({
          partId: d.partId,
          requestQty: d.requestQty,
          remark: d.remark,
          lineSort: idx,
        })),
      }
      this.applySubmitting = true
      try {
        let res
        if (this.applyDialogMode === 'edit') {
          payload.id = this.applyForm.id
          res = await updateApply(payload)
        } else {
          res = await add(payload)
        }
        const body = res && res.data
        if (body && body.success && (body.code === 200 || body.code === 2000)) {
          this.$message.success('提交成功')
          this.applyDialogVisible = false
          this.$emit('success')
        } else {
          this.$message.error((body && body.msg) || '操作失败')
        }
      } catch (e) {
        if (!e || !e.message) {
          this.$message.error('操作失败')
        }
      } finally {
        this.applySubmitting = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
.detail-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.detail-label {
  font-weight: 500;
  color: #606266;
}
</style>
